"use client";

import React, { useCallback, useEffect, useState, useRef } from "react";
import { Resizable } from "re-resizable";
import {
    SmartphoneIcon,
    Laptop2Icon,
    MonitorIcon,
    FullscreenIcon,
    Tv2Icon,
    ScanIcon,
    RectangleVertical,
} from "lucide-react";

import { Hint } from "../ui/hint";
import IframeDropZone from "../visual-editor/IframeDropZone";

import {
    VIEWPORT_SIZES,
    ViewportSizeType,
    ViewportSize,
} from "@/types/viewport-device.types";

interface ResponsiveIframeViewerProps
    extends React.HTMLAttributes<HTMLDivElement> {
    src: string;
    iframeRef: any;
    title: string;
    sandbox?: string;
    width?: number;
    height?: number;
    size?: ViewportSizeType;
    minWidth?: number;
    minHeight?: number;
    showControls?: boolean;
    enabledControls?: ViewportSizeType[];
    allowResizingY?: boolean;
    allowResizingX?: boolean;
    fluidX?: boolean;
    fluidY?: boolean;
    onIframeLoad?: (
        event: React.SyntheticEvent<HTMLIFrameElement, Event>
    ) => void;
    overrideViewportSizes?: Partial<
        Record<
            ViewportSizeType,
            {
                width: number | string;
                height: number | string;
            }
        >
    >;
    iframeClassName?: string;
    resizableContainerClassName?: string;
    controlsClassName?: string;
    controlsContainerClassName?: string;
    controlsPreComponent?: React.ReactNode;
    controlsPostComponent?: React.ReactNode;
    onFluidSelect?: () => void;
    onResizeStart?: () => void;
    onResizeStop?: (width: number, height: number) => void;
}

interface ViewportChangeButtonProps
    extends React.HTMLAttributes<HTMLButtonElement> {
    size: ViewportSizeType;
    selected?: boolean;
}

const ViewportChangeButton = React.forwardRef<
    HTMLButtonElement,
    ViewportChangeButtonProps
>((props, ref) => {
    const { size, ...rest } = props;
    const iconSize = 16;

    let icon = <FullscreenIcon size={iconSize} />;
    switch (size) {
        case ViewportSize.miniMobile:
            icon = <RectangleVertical size={iconSize} />;
            break;

        case ViewportSize.mobile:
        case ViewportSize.sm:
            icon = <SmartphoneIcon size={iconSize} />;
            break;
        case ViewportSize.tablet:
        case ViewportSize.md:
            icon = <Laptop2Icon size={iconSize} />;
            break;
        case ViewportSize.desktop:
        case ViewportSize.lg:
        case ViewportSize.xl:
            icon = <MonitorIcon size={iconSize} />;
            break;

        case ViewportSize["2xl"]:
        case ViewportSize["3xl"]:
            icon = <Tv2Icon size={iconSize} />;
            break;

        default:
            icon = <ScanIcon size={iconSize} />;
            break;
    }

    return (
        <button
            ref={ref}
            {...rest}
            className={[
                "border-0 p-1.5 rounded transition-colors duration-200",
                props.selected
                    ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                props.className,
            ].join(" ")}>
            {icon}
        </button>
    );
});

ViewportChangeButton.displayName = "ViewportChangeButton";

const CornerHandle = (props: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        {...props}
        className={[
            "relative left-[3px] top-[3px] w-2 h-2",
            "bg-transparent hover:bg-gray-400/50",
            "transition-colors duration-200",
            "cursor-nwse-resize",
            "rounded-full",
            props.className,
        ].join(" ")}
    />
);

const CustomHandle = (props: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        {...props}
        className={[
            "group absolute",
            "bg-transparent hover:bg-gray-800/30",
            "transition-colors duration-200",
            "flex items-center justify-center",
            props.className,
        ].join(" ")}
    />
);

const HorizontalHandle = ({ className }: { className?: string }) => (
    <CustomHandle className={className} />
);

const VerticalHandle = ({ className }: { className?: string }) => (
    <CustomHandle className={className} />
);

export const ResponsiveIframeViewer = (props: ResponsiveIframeViewerProps) => {
    const {
        width = VIEWPORT_SIZES.desktop.width,
        height = VIEWPORT_SIZES.desktop.height,
        size,
        minWidth = 200,
        minHeight = 200,
        enabledControls = [
            ViewportSize.mobile,
            ViewportSize.tablet,
            ViewportSize.desktop,
            ViewportSize.fluid,
        ],
        overrideViewportSizes,
        showControls = true,
        allowResizingY = false,
        allowResizingX = false,
        fluidX = false,
        fluidY = false,
        iframeClassName = "",
        resizableContainerClassName = "",
        controlsPreComponent,
        controlsPostComponent,
        controlsContainerClassName,
        controlsClassName,
        className,
        src,
        title,
        sandbox,
        onIframeLoad,
        iframeRef,
        onFluidSelect,
        onResizeStart,
        onResizeStop,
        ...iframeProps
    } = props;

    const getViewportSize = useCallback(() => {
        // Apply fluid settings first
        const fluidDimensions = {
            width: fluidX ? "100%" : width,
            height: fluidY ? "100%" : height,
        };

        // If size is specified and neither fluidX nor fluidY are set, use the viewport size
        if (size && !fluidX && !fluidY) {
            return VIEWPORT_SIZES[size];
        }

        // If size is specified but either fluidX or fluidY are set,
        // selectively override the dimensions
        if (size) {
            return {
                width: fluidX ? "100%" : VIEWPORT_SIZES[size].width,
                height: fluidY ? "100%" : VIEWPORT_SIZES[size].height,
            };
        }

        // Otherwise use the fluid dimensions (which might include width/height props)
        return fluidDimensions;
    }, [size, width, height, fluidX, fluidY]);

    const [viewportSizeInternal, setViewportSizeInternal] = useState<{
        width: number | string;
        height: number | string;
    }>(getViewportSize());

    // Ref and state for measuring actual container size
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 1200, height: 800 });

    // Measure container size on mount and resize
    useEffect(() => {
        const measureContainer = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                // Leave some padding (32px total)
                setContainerSize({
                    width: Math.max(rect.width - 32, 200),
                    height: Math.max(rect.height - 32, 200),
                });
            }
        };

        measureContainer();

        const resizeObserver = new ResizeObserver(measureContainer);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const updateViewportSize = useCallback(
        (
            {
                width,
                height,
            }: { width: number | string; height: number | string },
            size?: ViewportSizeType
        ) => {
            // Handle known viewports, applying overrides if necessary.
            if (size) {
                const viewportWidth =
                    overrideViewportSizes?.[size]?.width ||
                    VIEWPORT_SIZES[size].width;
                const viewportHeight =
                    overrideViewportSizes?.[size]?.height ||
                    VIEWPORT_SIZES[size].height;

                // Apply fluid settings on top of viewport sizes
                setViewportSizeInternal({
                    width: fluidX ? "100%" : viewportWidth,
                    height: fluidY ? "100%" : viewportHeight,
                });

                return;
            }

            // Arbitrary dimensions.
            if (typeof width === "number" && typeof height === "number") {
                const nextWidth = width < (minWidth || 0) ? minWidth : width;
                const nextHeight =
                    height < (minHeight || 0) ? minHeight : height;

                // Apply fluid settings for arbitrary dimensions
                setViewportSizeInternal({
                    width: fluidX ? "100%" : nextWidth,
                    height: fluidY ? "100%" : nextHeight,
                });
            } else {
                // If width/height are already strings (potentially "100%"),
                // respect fluidX/fluidY for explicit overrides
                setViewportSizeInternal({
                    width: fluidX ? "100%" : width,
                    height: fluidY ? "100%" : height,
                });
            }
        },
        [
            setViewportSizeInternal,
            minWidth,
            minHeight,
            overrideViewportSizes,
            fluidX,
            fluidY,
        ]
    );

    const isSizeSelected = (size: ViewportSizeType) => {
        return (
            viewportSizeInternal.width === VIEWPORT_SIZES[size].width &&
            viewportSizeInternal.height === VIEWPORT_SIZES[size].height
        );
    };

    const isFluidMode =
        viewportSizeInternal.width === "100%" &&
        viewportSizeInternal.height === "100%";

    const getDisplayTextForDimensions = () => {
        const currentWidth =
            typeof viewportSizeInternal.width === "number"
                ? viewportSizeInternal.width
                : 0;

        const selectedSize = enabledControls.find((size) =>
            isSizeSelected(size)
        );
        if (selectedSize) {
            return `${selectedSize.charAt(0).toUpperCase() + selectedSize.slice(1)} (${currentWidth}x${viewportSizeInternal.height})`;
        }

        if (isFluidMode) {
            return "Fluid (100%)";
        }

        if (currentWidth > 0) {
            let breakpointName = "Desktop";

            if (currentWidth <= 768) {
                breakpointName = "Mobile";
            } else if (currentWidth <= 1024) {
                breakpointName = "Tablet";
            } else {
                breakpointName = "Desktop";
            }

            return `${breakpointName} (${currentWidth}x${viewportSizeInternal.height})`;
        }

        return "Desktop";
    };

    const Controls = () => {
        const displayText = getDisplayTextForDimensions();

        return showControls ? (
            <div
                className={[
                    "flex items-center bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-3 py-2 gap-4",
                    controlsContainerClassName,
                ].join(" ")}>
                {controlsPreComponent}
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px]">
                    {displayText}
                </span>
                <div
                    className={[
                        "flex items-center gap-1",
                        controlsClassName,
                    ].join(" ")}>
                    {enabledControls.map((size) => {
                        return (
                            <Hint
                                text={size.toUpperCase()}
                                key={size}
                                side="top"
                                align="center"
                                delayDuration={300}>
                                <ViewportChangeButton
                                    key={size}
                                    size={size}
                                    onClick={() => {
                                        if (
                                            size === ViewportSize.fluid &&
                                            onFluidSelect
                                        ) {
                                            onFluidSelect();
                                        } else {
                                            updateViewportSize(
                                                VIEWPORT_SIZES[size],
                                                size
                                            );
                                        }
                                    }}
                                    selected={isSizeSelected(size)}
                                />
                            </Hint>
                        );
                    })}
                </div>
                {controlsPostComponent}
            </div>
        ) : null;
    };

    useEffect(() => {
        updateViewportSize(getViewportSize(), size);
    }, [getViewportSize, updateViewportSize, size]);

    if (!allowResizingX && !allowResizingY) {
        // For non-resizable mode, use dynamically measured container size
        const viewportWidthNoResize = typeof viewportSizeInternal.width === 'number' ? viewportSizeInternal.width : containerSize.width;
        const viewportHeightNoResize = typeof viewportSizeInternal.height === 'number' ? viewportSizeInternal.height : containerSize.height;

        // Calculate scale based on actual container size (measured dynamically)
        const scaleXNoResize = viewportWidthNoResize > containerSize.width ? containerSize.width / viewportWidthNoResize : 1;
        const scaleYNoResize = viewportHeightNoResize > containerSize.height ? containerSize.height / viewportHeightNoResize : 1;
        const scaleNoResize = Math.min(scaleXNoResize, scaleYNoResize, 1); // Never scale up, only down

        const scaledWidthNoResize = viewportWidthNoResize * scaleNoResize;
        const scaledHeightNoResize = viewportHeightNoResize * scaleNoResize;

        return (
            <div
                ref={containerRef}
                className={[
                    "h-full w-full flex flex-col overflow-hidden",
                    isFluidMode ? "" : "gap-4 items-center justify-center",
                    className,
                ].join(" ")}>
                {!isFluidMode && <Controls />}
                <div className={isFluidMode ? "h-full w-full" : "flex items-center justify-center w-full h-full overflow-hidden"}>
                    {isFluidMode ? (
                        <iframe
                            {...iframeProps}
                            src={src}
                            ref={iframeRef}
                            sandbox={sandbox}
                            title={title}
                            className={[
                                "border-none w-full h-full",
                                iframeClassName,
                            ].join(" ")}
                            onLoad={onIframeLoad}
                        />
                    ) : (
                        <IframeDropZone
                            className="overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm rounded"
                            style={{
                                width: `${scaledWidthNoResize}px`,
                                height: `${scaledHeightNoResize}px`,
                            }}>
                            <iframe
                                {...iframeProps}
                                src={src}
                                ref={iframeRef}
                                sandbox={sandbox}
                                title={title}
                                className="border-none"
                                style={{
                                    width: `${viewportWidthNoResize}px`,
                                    height: `${viewportHeightNoResize}px`,
                                    transform: `scale(${scaleNoResize})`,
                                    transformOrigin: 'top left',
                                }}
                                onLoad={onIframeLoad}
                            />
                        </IframeDropZone>
                    )}
                </div>
            </div>
        );
    }

    // Calculate scale to fit large viewports in available space (using dynamic container size)
    const viewportWidth = typeof viewportSizeInternal.width === 'number' ? viewportSizeInternal.width : containerSize.width;
    const viewportHeight = typeof viewportSizeInternal.height === 'number' ? viewportSizeInternal.height : containerSize.height;

    const scaleX = viewportWidth > containerSize.width ? containerSize.width / viewportWidth : 1;
    const scaleY = viewportHeight > containerSize.height ? containerSize.height / viewportHeight : 1;
    const scale = Math.min(scaleX, scaleY, 1); // Never scale up, only down

    const scaledWidth = viewportWidth * scale;
    const scaledHeight = viewportHeight * scale;

    return (
        <div
            ref={containerRef}
            className={[
                "h-full w-full flex flex-col",
                isFluidMode ? "" : "gap-4 items-center justify-center",
                className,
            ].join(" ")}>
            {!isFluidMode && <Controls />}

            <Resizable
                defaultSize={{
                    width: scaledWidth,
                    height: scaledHeight,
                }}
                size={{
                    width: scaledWidth,
                    height: scaledHeight,
                }}
                minWidth={
                    viewportSizeInternal.width === "100%" ? undefined : minWidth
                }
                minHeight={
                    viewportSizeInternal.height === "100%"
                        ? undefined
                        : minHeight
                }
                onResizeStart={() => {
                    if (onResizeStart) {
                        onResizeStart();
                    }
                }}
                onResizeStop={(_e, _direction, ref, d) => {
                    let newWidth: number;
                    let newHeight: number;

                    if (
                        typeof viewportSizeInternal.width === "number" &&
                        typeof viewportSizeInternal.height === "number"
                    ) {
                        newWidth = viewportSizeInternal.width + d.width;
                        newHeight = viewportSizeInternal.height + d.height;
                        updateViewportSize({
                            width: newWidth,
                            height: newHeight,
                        });
                    } else {
                        const existingSize = ref.getBoundingClientRect();
                        newWidth = existingSize.width + d.width;
                        newHeight = existingSize.height + d.height;

                        updateViewportSize({
                            width: newWidth,
                            height: newHeight,
                        });
                    }

                    if (onResizeStop) {
                        onResizeStop(newWidth, newHeight);
                    }
                }}
                handleComponent={{
                    // top: <HorizontalHandle />,
                    right: (
                        <VerticalHandle className="w-1 h-full cursor-ew-resize !right-0" />
                    ),
                    bottom: (
                        <HorizontalHandle className="h-1 w-full cursor-ns-resize !bottom-0 !left-0" />
                    ),
                    // left: <VerticalHandle />,
                    bottomRight:
                        allowResizingX && allowResizingY ? (
                            <CornerHandle />
                        ) : undefined,
                }}
                enable={{
                    top: false, // top: allowResizingY,
                    right: allowResizingX,
                    bottom: allowResizingY,
                    left: false, // left: allowResizingX,
                    topRight: false,
                    bottomRight: true,
                    bottomLeft: false,
                    topLeft: false,
                }}
                className={[
                    "hover:transition-none transition-all",
                    "border border-gray-200 dark:border-gray-700 shadow-sm",
                    "hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md",
                    resizableContainerClassName,
                ].join(" ")}>
                <IframeDropZone
                    className="w-full h-full overflow-hidden"
                    style={{
                        width: `${scaledWidth}px`,
                        height: `${scaledHeight}px`,
                    }}>
                    <iframe
                        {...iframeProps}
                        src={src}
                        ref={iframeRef}
                        sandbox={sandbox}
                        title={title}
                        className={[
                            "border-none",
                            iframeClassName,
                        ].join(" ")}
                        style={{
                            width: `${viewportWidth}px`,
                            height: `${viewportHeight}px`,
                            transform: `scale(${scale})`,
                            transformOrigin: 'top left',
                        }}
                        onLoad={onIframeLoad}
                    />
                </IframeDropZone>
            </Resizable>
        </div>
    );
};
